import axios from 'axios';
import instance from '../../api/axios-instance';
import type { ApiException } from '../../../types';
import {
  allowlistedKnowledgeSources,
  type SupportAgentSource,
} from './support-agent-sources';

export type SupportAgentMessageResponse = {
  reply: string;
  sessionId: string;
  sources?: SupportAgentSource[];
};

export type SupportAgentReply = {
  reply: string;
  sources: SupportAgentSource[];
};

function toAbortError(cause?: unknown): DOMException {
  const error = new DOMException('Aborted', 'AbortError');
  if (cause !== undefined) {
    try {
      Object.defineProperty(error, 'cause', { value: cause });
    } catch {
      // ignore if cause cannot be set
    }
  }
  return error;
}

function extractApiMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
      throw toAbortError(error);
    }

    const data = error.response?.data as ApiException | undefined;
    if (data?.message) {
      return data.message;
    }

    if (error.response?.status === 403) {
      return 'Support agent is not enabled for this domain.';
    }

    if (error.response?.status === 429) {
      return 'Too many support agent messages. Please wait a minute and try again.';
    }

    if (error.response?.status === 503) {
      return 'Support agent is not configured. Please try again later.';
    }

    if (error.response?.status === 502) {
      return 'Support agent is temporarily unavailable. Please try again.';
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
}

/** POST /api/support-agent/message — returns the assistant reply and allowlisted sources. */
export async function requestSupportReply(
  prompt: string,
  signal?: AbortSignal
): Promise<SupportAgentReply> {
  try {
    const { data } = await instance.post<SupportAgentMessageResponse>(
      '/api/support-agent/message',
      { message: prompt },
      { signal }
    );

    if (!data?.reply?.trim()) {
      throw new Error(
        'Support agent returned an empty response. Please try again.'
      );
    }

    return {
      reply: data.reply,
      sources: allowlistedKnowledgeSources(data.sources),
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new Error(extractApiMessage(error));
  }
}

/** DELETE /api/support-agent/session — starts a new Dialogflow CX session. */
export async function clearSupportAgentSession(
  signal?: AbortSignal
): Promise<void> {
  try {
    await instance.delete('/api/support-agent/session', { signal });
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      (error.code === 'ERR_CANCELED' || error.name === 'CanceledError')
    ) {
      throw toAbortError(error);
    }
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new Error(extractApiMessage(error));
  }
}
